import { SetStateAction, useState } from "react";

import { useTranslation } from "react-i18next";

import { useDashboardStore } from "@/stores/useDashboardStore";
import {
  resetAllTableStoreFiltersAndDetailsData,
  useTableStoreByRoute
} from "@/stores/useTableStore";
import { useTokenRefreshLoadingStore } from "@/stores/useTokenRefreshLoadingStore";

import { useAuth } from "../../../../../../context/Auth/AuthProvider";
import { CompaniesDto, ROUTES, TUTORIAL_IDS } from "../../../../../../shared";
import { useReleaseFeatureToggle } from "../../../../../../shared/featureToggles/hooks";
import { FEATURE_NAMES } from "../../../../../../shared/featureToggles/types";
import useChangeSelectedCompany from "../../hooks/useChangeSelectedCompany";
import CompanyList from "./components/CompanyList/CompanyList";
import CompanySelectorTopControls from "./components/CompanySelectorTopControls/CompanySelectorTopControls";
import RootCompany from "./components/RootCompany/RootCompany";
import SearchCompany from "./components/SearchCompany/SearchCompany";

interface ICompanySelectorContentProps {
  route: ROUTES;
  companies: CompaniesDto[];
  close: () => void;
}

const CompanySelectorContent = ({
  route,
  companies,
  close
}: ICompanySelectorContentProps) => {
  const { user } = useAuth();
  const { isReleaseFeatureEnabledForRole } = useReleaseFeatureToggle();
  const { changeSelectedCompany } = useChangeSelectedCompany();

  const { loading: isTableLoading } = useTableStoreByRoute(route);
  const isDashboardLoading = useDashboardStore(
    state => state.isDashboardLoading
  );

  const isTokenRefreshLoading = useTokenRefreshLoadingStore(
    state => state.isTokenRefreshLoading
  );

  const { t } = useTranslation();

  const [searchValue, setSearchValue] = useState("");

  const [selectedCompany, setSelectedCompany] = useState<CompaniesDto | string>(
    user.selectedCompany.id
  );

  const handleSearchValue = (e: {
    target: { value: SetStateAction<string> };
  }) => setSearchValue(e.target.value);

  const clearSearchValue = () => setSearchValue("");

  const handleChangeSelection = async (
    value: string,
    shouldQueryData?: boolean
  ) => {
    if (!isTokenRefreshLoading) {
      resetAllTableStoreFiltersAndDetailsData();

      setSelectedCompany(value);

      await changeSelectedCompany(value, true, undefined, shouldQueryData);
    }
  };

  const isRoleAllowedToChangeRootCompany = isReleaseFeatureEnabledForRole(
    FEATURE_NAMES.AllowedToChangeRootCompany,
    user?.role
  );

  const isDisabled =
    route === ROUTES.Dashboard ? isDashboardLoading : isTableLoading;

  return (
    <div id={TUTORIAL_IDS.CompanySelectorContent}>
      <CompanySelectorTopControls
        close={close}
        boxPadding="16px 16px 12px 16px"
        itemName={t("Selector##company selector")}
      />
      {isRoleAllowedToChangeRootCompany && (
        <RootCompany
          isDisabled={isDisabled}
          userRootCompany={user?.selectedRootCompany}
          handleChangeSelection={handleChangeSelection}
        />
      )}
      <SearchCompany
        isDisabled={isDisabled}
        isRootSearch={isRoleAllowedToChangeRootCompany}
        searchValue={searchValue}
        handleSearchValue={handleSearchValue}
        clearSearchValue={clearSearchValue}
      />

      <CompanyList
        route={route}
        searchValue={searchValue}
        companies={companies}
        selectedCompany={selectedCompany}
        handleChangeSelection={handleChangeSelection}
      />
    </div>
  );
};

export default CompanySelectorContent;
